/**
 * assets/component-configurator.js
 * ------------------------------------------------------------------
 * Core controller for the Atio configurator. Owns state, all DOM
 * events (sliders, fabric drawer, inquiry form), and price/summary
 * text updates.
 *
 * It does NOT know how to draw a sofa. Drawing is delegated per active
 * view to a renderer registered on window.AtioConfigurator.renderers:
 *
 *   window.AtioConfigurator.renderers['2d'].render(ctx)   -> component-configurator-2d.js
 *   window.AtioConfigurator.renderers['3d'].render(ctx)   -> component-configurator-3d.js
 *
 * ctx passed to a renderer's render(ctx):
 *   { root, overlay, svgGroup, view3dRoot, state, unit, backDepth,
 *     standardWidth, standardDepth }
 *
 * Load order (see blocks/product-configurator.liquid): the 2D and 3D
 * scripts load (and self-register) before this file runs its boot().
 */
(function () {
  'use strict';

  function qs(root, selector) { return root.querySelector(selector); }
  function qsa(root, selector) { return Array.prototype.slice.call(root.querySelectorAll(selector)); }
  function clamp(value, min, max) { return Math.max(min, Math.min(max, Number(value) || min)); }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function money(cents) {
    return '€' + Math.round(Number(cents || 0) / 100).toLocaleString('it-IT') + ',00';
  }

  /* ── Pricing engine ───────────────────────────────────────────────
   * Ported from the "Atio configurator pricing engine" reference impl.
   * Logic (unchanged from the reference):
   *   - Standard (site) dimensions are the anchor: unmodified elements
   *     return the exact site price (rounded to PRICING_ROUND_TO).
   *   - Custom dimensions shift production cost + Fercam shipping cost;
   *     only that DELTA (marked up + VAT'd) is applied on top of the
   *     anchor — the retail price is never rebuilt from cost alone.
   *   - Seat equivalent size = width + depth - 72; chaise = width + depth - 138.
   *   - Equivalent size snaps to the nearest COST_TIERS row (ties -> higher tier).
   *   - Fercam shipping cost is banded volumetric weight (m³ × 400kg,
   *     rounded up to the next 100kg, minimum 200kg).
   *
   * The one thing adapted from the reference: instead of a hardcoded
   * BASES table keyed by SKU, both the "standard" and "custom" element
   * lists are derived from data-composition-json — the standard list at
   * the product's default (site) width/depth, the custom list at the
   * CURRENT state.width/state.depth — via buildElements() below. That
   * reuses the same %-vs-flat-cm resolution as the rest of this file.
   * ─────────────────────────────────────────────────────────────── */

  var COST_TIERS = [
    { cm: 50,  seat: 79.9,     fixedBack: 59.16,    arm: 73.712, chaise: 143.82,   mobileBack: 59 },
    { cm: 60,  seat: 90.1,     fixedBack: 63.8775,  arm: 73.712, chaise: 162.18,   mobileBack: 59 },
    { cm: 70,  seat: 100.3,    fixedBack: 68.595,   arm: 73.712, chaise: 180.54,   mobileBack: 59 },
    { cm: 80,  seat: 110.5,    fixedBack: 73.3125,  arm: 73.712, chaise: 198.9,    mobileBack: 59 },
    { cm: 90,  seat: 120.7,    fixedBack: 78.03,    arm: 73.712, chaise: 217.26,   mobileBack: 59 },
    { cm: 100, seat: 130.9595, fixedBack: 82.7509,  arm: 73.712, chaise: 235.7271, mobileBack: 59 },
    { cm: 105, seat: 134.4105, fixedBack: 85.1105,  arm: 73.712, chaise: 241.9389, mobileBack: 59 },
    { cm: 110, seat: 137.8615, fixedBack: 87.4718,  arm: 73.712, chaise: 248.1507, mobileBack: 59 },
    { cm: 120, seat: 144.755,  fixedBack: 92.1927,  arm: 73.712, chaise: 260.559,  mobileBack: 59 },
    { cm: 130, seat: 151.657,  fixedBack: 96.9136,  arm: 73.712, chaise: 272.9826, mobileBack: 59 },
    { cm: 135, seat: 155.108,  fixedBack: 99.27575, arm: 73.712, chaise: 279.1944, mobileBack: 59 },
    { cm: 140, seat: 158.5505, fixedBack: 101.6345, arm: 73.712, chaise: 285.3909, mobileBack: 59 },
    { cm: 150, seat: 164.1265, fixedBack: 107.3295, arm: 73.712, chaise: 295.4277, mobileBack: 59 },
    { cm: 160, seat: 169.7025, fixedBack: 113.0245, arm: 73.712, chaise: 305.4645, mobileBack: 59 },
    { cm: 170, seat: 175.2785, fixedBack: 118.7195, arm: 73.712, chaise: 315.5013, mobileBack: 59 },
    { cm: 180, seat: 180.8545, fixedBack: 124.4145, arm: 73.712, chaise: 325.5381, mobileBack: 59 },
    { cm: 190, seat: 186.422,  fixedBack: 130.1095, arm: 73.712, chaise: 335.5596, mobileBack: 59 },
    { cm: 200, seat: 191.998,  fixedBack: 135.8045, arm: 73.712, chaise: 345.5964, mobileBack: 59 },
    { cm: 210, seat: 197.574,  fixedBack: 141.4995, arm: 73.712, chaise: 355.6332, mobileBack: 59 },
    { cm: 220, seat: 203.15,   fixedBack: 147.237,  arm: 73.712, chaise: 365.67,   mobileBack: 59 }
  ];

  var FERCAM_COST = {
    200: 54.76,
    300: 75.89,
    400: 94.52,
    500: 113.15,
    600: 131.78,
    700: 150.41,
    800: 169.04,
    900: 187.60
  };

  /* Pricing knobs from the reference calculateAtioPrice() defaults.
   * Plain constants for now — flag if you want these driven by block
   * settings instead. */
  var PRICING_MARKUP = 1.65;
  var PRICING_VAT = 0.22;
  var PRICING_PRODUCTION_COMMISSION = 0;
  var PRICING_ROUND_TO = 10;

  /* A composition-JSON w/d value is either a flat cm number (number or
   * plain numeric string), or a string percentage of `referenceCm`. */
  function resolveDimension(raw, referenceCm) {
    if (typeof raw === 'string' && raw.trim().slice(-1) === '%') {
      var pct = parseFloat(raw) || 0;
      return (pct / 100) * referenceCm;
    }
    return parseFloat(raw) || 0;
  }

  function parseComposition(json) {
    try {
      var parsed = JSON.parse(json || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /* Composition-JSON types ("seat", "backrest", "armrest") don't match
   * the pricing engine's vocabulary 1:1 — map them here. "chaise" is
   * passed through as-is if a composition element is literally tagged
   * that way (there's no auto-detection from a seat's size or from the
   * penisola_position metafield — flag if that mapping should exist).
   * "backrest" becomes "fixedBack" or "mobileBack" depending on family,
   * since only DAN-family products use loose/mobile back cushions. */
  function mapElementType(compositionType, family) {
    switch (compositionType) {
      case 'seat':
      case 'chaise':
        return compositionType;
      case 'backrest':
        return family === 'DAN' ? 'mobileBack' : 'fixedBack';
      case 'armrest':
        return 'arm';
      default:
        return null; // unrecognized element type — excluded from pricing
    }
  }

  /* Fixed heights per engine element type (composition JSON only carries
   * width/depth). Only used for Fercam volume — componentCost() below
   * never reads height. seat/fixedBack/arm match the values already used
   * for the 2D preview; mobileBack has no equivalent there yet, so it's
   * taken from the one DAN reference example (70x37x34 cm back cushion). */
  var ENGINE_HEIGHT_CM = { seat: 40, chaise: 40, fixedBack: 70, mobileBack: 34, arm: 55 };

  /* Resolves data-composition-json into engine-ready {type, width, depth,
   * height} elements at a given total width/depth. Reference spans for
   * %-based w/d (same rules used elsewhere in this file):
   *   - armrest width/depth: % of the full total width/depth
   *   - seat/backrest width: % of (total width - sum of armrest widths)
   *   - seat depth: % of (total depth - backDepthCm)
   *   - backrest depth: % of the full total depth
   */
  function buildElements(compositionElements, totalWidth, totalDepth, backDepthCm, family) {
    var armrestWidthTotal = 0;
    compositionElements.forEach(function (el) {
      if (el.type === 'armrest') armrestWidthTotal += resolveDimension(el.w, totalWidth);
    });

    var widthRemainder = Math.max(totalWidth - armrestWidthTotal, 0);
    var seatDepthRemainder = Math.max(totalDepth - backDepthCm, 0);

    var elements = [];

    compositionElements.forEach(function (el) {
      var engineType = mapElementType(el.type, family);
      if (!engineType) return; // skip elements the pricing engine can't cost

      var isArmrest = el.type === 'armrest';
      var isSeatLike = el.type === 'seat' || el.type === 'chaise';

      var widthRef = isArmrest ? totalWidth : widthRemainder;
      var depthRef = isSeatLike ? seatDepthRemainder : totalDepth;

      elements.push({
        type: engineType,
        width: resolveDimension(el.w, widthRef),
        depth: resolveDimension(el.d, depthRef),
        height: ENGINE_HEIGHT_CM[engineType]
      });
    });

    return elements;
  }

  function nearestTier(value) {
    return COST_TIERS.reduce(function (best, tier) {
      var distance = Math.abs(tier.cm - value);
      var bestDistance = Math.abs(best.cm - value);

      if (distance < bestDistance) return tier;
      // If exactly halfway between two tiers, use the higher tier.
      if (distance === bestDistance && tier.cm > best.cm) return tier;

      return best;
    }, COST_TIERS[0]);
  }

  function equivalentCm(element) {
    switch (element.type) {
      case 'seat': return element.width + element.depth - 72;
      case 'chaise': return element.width + element.depth - 138;
      case 'fixedBack': return element.width;
      case 'arm':
      case 'mobileBack':
        return null;
      default:
        throw new Error('Unknown element type: ' + element.type);
    }
  }

  function componentCost(element) {
    if (element.type === 'arm') return COST_TIERS[0].arm;
    if (element.type === 'mobileBack') return COST_TIERS[0].mobileBack;

    var tier = nearestTier(equivalentCm(element));

    if (element.type === 'seat') return tier.seat;
    if (element.type === 'chaise') return tier.chaise;
    if (element.type === 'fixedBack') return tier.fixedBack;

    throw new Error('Unknown element type: ' + element.type);
  }

  /* Dan is special: useful seat depth is e.g. 80cm, but the packed body
   * has an additional 35cm depth occupied by the movable backs. */
  function packageVolumeM3(element, family) {
    var packageDepth = (family === 'DAN' && element.type === 'seat')
      ? element.depth + 35
      : element.depth;

    return (element.width * packageDepth * element.height) / 1000000;
  }

  function calculateFercam(elements, family) {
    var volumeM3 = elements.reduce(function (sum, element) {
      return sum + packageVolumeM3(element, family);
    }, 0);

    var volumetricKg = volumeM3 * 400;
    var bandKg = Math.max(200, Math.ceil(volumetricKg / 100) * 100);

    var cost;
    if (FERCAM_COST[bandKg] !== undefined) {
      cost = FERCAM_COST[bandKg];
    } else if (bandKg > 900) {
      // Extension of the existing tariff progression.
      cost = 187.60 + ((bandKg - 900) / 100) * 18.63;
    } else {
      cost = FERCAM_COST[200];
    }

    return { volumeM3: volumeM3, volumetricKg: volumetricKg, bandKg: bandKg, cost: cost };
  }

  function ceilTo(value, step) {
    step = step || 10;
    return Math.ceil(value / step) * step;
  }

  var eyeOpen = '<svg viewBox="0 0 24 24" fill="none"><path d="M2.8 12s3.2-5.2 9.2-5.2S21.2 12 21.2 12s-3.2 5.2-9.2 5.2S2.8 12 2.8 12Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="12" r="2.8" stroke="currentColor" stroke-width="1.5"/></svg>';
  var eyeClosed = '<svg viewBox="0 0 24 24" fill="none"><path d="M3 3l18 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M9.6 5.2A9.5 9.5 0 0 1 12 4.9c6 0 9.2 5.2 9.2 5.2a15.6 15.6 0 0 1-2.6 3.1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M14.1 14.1A2.8 2.8 0 0 1 10 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M6.5 6.9A15.2 15.2 0 0 0 2.8 12s3.2 5.2 9.2 5.2c1 0 1.9-.14 2.7-.38" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function init(root) {
    var state = {
      width: clamp(Number(root.dataset.widthDefault) || 140, Number(root.dataset.widthMin) || 50, Number(root.dataset.widthMax) || 270),
      depth: clamp(Number(root.dataset.depthDefault) || 107, Number(root.dataset.depthMin) || 90, Number(root.dataset.depthMax) || 125),
      fabric: 'S13',
      fabricColor: '#d8d8d4',
      materialName: null, // set once loadFabrics() resolves the default fabric's atio-material tag
      fabricCollection: root.dataset.defaultMaterialCollection || 'flow', // set once loadFabrics() resolves the default fabric's atio-collection tag
      showDetails: true,
      contactMethod: null,
      dirtyDimensions: false,
      requestType: 'info',
      activeTab: 'general',
      view: root.dataset.view === '3d' ? '3d' : '2d'
    };

    var unit = root.dataset.unit || 'cm';
    var backDepth = Number(root.dataset.backDepth || 35);
    var standardWidth = Number(root.dataset.a1StandardWidth || 140);
    var standardDepth = Number(root.dataset.a1StandardDepth || 107);
    var basePrice = Number(root.dataset.basePrice || 149000);
    var pricePerCm = Number(root.dataset.pricePerCm || 400); // no longer used by price() (see volume formula below); kept for potential future use
    var productTitle = root.dataset.productTitle || '';
    var shippingDate = root.dataset.shippingDate || '20 luglio';
    var fabricCollectionHandle = root.dataset.fabricCollection || 'rivestimenti';
    var compositionElements = parseComposition(root.dataset.compositionJson);

    // "MAX" vs "DAN" changes fixedBack->mobileBack mapping and Fercam's
    // seat-depth packaging adjustment (see buildElements/packageVolumeM3
    // above). Sourced from data-product-family (see product-configurator.liquid);
    // defaults to MAX, the more common family, if that attribute is absent.
    var family = (root.dataset.productFamily || 'MAX').toUpperCase();

    // The anchor/"standard" configuration for delta pricing: the same
    // composition, resolved at the product's default (site) dimensions
    // rather than the current slider position. Fixed per instance since
    // neither the composition nor the default dimensions change at runtime.
    var standardElements = buildElements(compositionElements, standardWidth, standardDepth, backDepth, family);

    var overlay = qs(root, '[data-atio-overlay]');

    /* Move the modal overlay directly under <body>.
       This prevents Shopify theme headers, sticky bars, and transformed product wrappers
       from appearing above the configurator. */
    if (overlay && overlay.parentNode !== document.body) {
      document.body.appendChild(overlay);
    }

    var svgGroup = qs(overlay, '[data-sofa-svg]');
    var view3dRoot = qs(overlay, '[data-view-3d-root]');
    var eye = qs(overlay, '[data-eye-icon]');
    var nativeForm = qs(root, '.atio-cfg__native-form');

    var previewEl = qs(overlay, '[data-atio-preview]');
    var view2dEl = qs(overlay, '[data-view-2d]');
    var view3dEl = qs(overlay, '[data-view-3d]');
    var viewToggleButtons = qsa(overlay, '[data-view-btn]');

    // Pending close() fallback timer (see close() below) — tracked per
    // instance since each configurator on the page has its own overlay.
    var closeFallbackTimer = null;

    function open() {
      if (!overlay.hidden) return; // already open

      overlay.hidden = false;

      // Force a reflow so the browser commits THIS frame — overlay
      // unhidden, but still at its CSS resting (faded/offset) values,
      // since .is-open hasn't been added yet — before the class below
      // changes anything further. Without this, both changes would
      // land in the same frame and there'd be nothing for the
      // transition to animate from. Skipped under reduced motion
      // since there's nothing to animate either way.
      //
      // The actual transition values (.18s ease, matching the only
      // other transition already in the stylesheet — .atio-cfg__
      // chevron) live in assets/component-configurator.css:
      // .atio-cfg__overlay/.atio-cfg__shell define the faded/offset
      // resting state, and .is-open defines the visible one.
      if (!prefersReducedMotion()) void overlay.offsetHeight;

      overlay.classList.add('is-open');
      document.body.classList.add('atio-cfg-open');
      render();

      if (window.requestIdleCallback) {
        requestIdleCallback(loadFabrics, { timeout: 1500 });
      } else {
        setTimeout(loadFabrics, 300);
      }
    }

    function close() {
      if (overlay.hidden || !overlay.classList.contains('is-open')) return; // already closed/closing

      overlay.classList.remove('is-open');
      document.body.classList.remove('atio-cfg-open');
      clearTimeout(closeFallbackTimer);

      if (prefersReducedMotion()) {
        overlay.hidden = true;
        return;
      }

      // Wait for the fade-out to actually finish before hiding, so
      // closing gets the same smooth treatment as opening rather than
      // cutting the transition off instantly. transitionend is the
      // real signal; the timeout is a safety net in case it never
      // fires (e.g. component-configurator.css's duration changes and
      // drifts out of sync, or some other style rule interferes) —
      // either way, whichever happens first finishes the close, and
      // cancels the other. 250ms comfortably clears the real .18s
      // transition without leaving an oversized fallback window.
      var finished = false;
      function finish() {
        if (finished) return;
        finished = true;
        overlay.removeEventListener('transitionend', onTransitionEnd);
        clearTimeout(closeFallbackTimer);
        overlay.hidden = true;
      }
      function onTransitionEnd(event) {
        if (event.target === overlay && event.propertyName === 'opacity') finish();
      }

      overlay.addEventListener('transitionend', onTransitionEnd);
      closeFallbackTimer = setTimeout(finish, 250);
    }

    function price() {
      try {
        var customElements = buildElements(compositionElements, state.width, state.depth, backDepth, family);

        var baseProduction = standardElements.reduce(function (sum, el) { return sum + componentCost(el); }, 0);
        var customProduction = customElements.reduce(function (sum, el) { return sum + componentCost(el); }, 0);

        var baseFercam = calculateFercam(standardElements, family);
        var customFercam = calculateFercam(customElements, family);

        // We do NOT reconstruct the whole retail price from cost — the
        // site price is the anchor, and only the delta customization
        // generates (marked up + VAT'd) is applied on top of it.
        var productionDelta = customProduction - baseProduction;
        var fercamDelta = customFercam.cost - baseFercam.cost;

        var productionFactor = (1 + PRICING_PRODUCTION_COMMISSION) * PRICING_MARKUP * (1 + PRICING_VAT);
        var fercamFactor = PRICING_MARKUP * (1 + PRICING_VAT);

        var retailDelta = productionDelta * productionFactor + fercamDelta * fercamFactor;

        var sitePriceEur = basePrice / 100; // basePrice (data-base-price) is cents, e.g. Shopify variant.price
        var rawPriceEur = sitePriceEur + retailDelta;
        var priceEur = ceilTo(rawPriceEur, PRICING_ROUND_TO);

        var cents = priceEur * 100; // basePrice/money() are both cents-denominated
        return cents > 0 ? cents : basePrice;
      } catch (err) {
        // Composition layout has an element type the engine can't cost —
        // fall back to the flat site price rather than breaking the UI.
        return basePrice;
      }
    }

    function compositionLabel() {
      return root.dataset.cfgComposition || 'Composizione A1';
    }

    function setRange(type) {
      var input = qs(overlay, '[data-range="' + type + '"]');
      if (!input) return;

      var min = Number(input.min) || (type === 'width' ? 50 : 90);
      var max = Number(input.max) || (type === 'width' ? 270 : 125);

      if (max <= min) {
        max = type === 'width' ? 270 : 125;
        min = type === 'width' ? 50 : 90;
      }

      var value = clamp(state[type], min, max);
      state[type] = value;

      var pct = ((value - min) / (max - min)) * 100;
      pct = Math.max(0, Math.min(100, pct));

      input.min = min;
      input.max = max;
      input.value = value;

      var valueEl = type === 'width'
        ? qs(overlay, '[data-width-value]')
        : qs(overlay, '[data-depth-value]');

      var fill = type === 'width'
        ? qs(overlay, '[data-width-fill]')
        : qs(overlay, '[data-depth-fill]');

      var knob = type === 'width'
        ? qs(overlay, '[data-width-knob]')
        : qs(overlay, '[data-depth-knob]');

      var originMarker = qs(overlay, '[data-origin-marker="' + type + '"]');
      var originValue = type === 'width' ? Number(root.dataset.widthDefault) || 140 : Number(root.dataset.depthDefault) || 107;
      var originPct = ((originValue - min) / (max - min)) * 100;
      originPct = Math.max(0, Math.min(100, originPct));

      if (valueEl) valueEl.value = value;
      if (fill) fill.style.width = pct + '%';
      if (knob) {
        knob.style.left = pct + '%';
        knob.style.transform = 'translateX(-50%)';
      }
      if (originMarker) {
        originMarker.style.left = originPct + '%';
        originMarker.setAttribute('data-origin-value', originValue + ' cm');
      }
    }

    /* ── View dispatch (2D / 3D) ─────────────────────────────────────
     * Delegates drawing to whichever renderer is registered for the
     * active view. If a renderer isn't registered (e.g. the 3D script
     * hasn't shipped yet), this is a silent no-op — the placeholder
     * markup in configurator-preview-3d.liquid stays as-is.
     */
    function renderActiveView() {
      var registry = window.AtioConfigurator && window.AtioConfigurator.renderers;
      var renderer = registry && registry[state.view];
      if (!renderer || typeof renderer.render !== 'function') return;

      renderer.render({
        root: root,
        overlay: overlay,
        svgGroup: svgGroup,
        view3dRoot: view3dRoot,
        state: state,
        unit: unit,
        backDepth: backDepth,
        standardWidth: standardWidth,
        standardDepth: standardDepth
      });
    }

    function syncViewVisibility() {
      if (previewEl) previewEl.dataset.view = state.view;
      if (view2dEl) view2dEl.hidden = state.view !== '2d';
      if (view3dEl) view3dEl.hidden = state.view !== '3d';

      viewToggleButtons.forEach(function (btn) {
        btn.classList.toggle('is-active', btn.dataset.viewBtn === state.view);
      });
    }

    function render() {
      var seatDepth = state.depth - backDepth;
      var total = state.width + ' × ' + state.depth + ' ' + unit;

      setRange('width');
      setRange('depth');
      renderActiveView();

      if (eye) eye.innerHTML = state.showDetails ? eyeOpen : eyeClosed;

      qsa(overlay, '[data-total-dimensions], [data-summary-dimensions], [data-summary-measure]').forEach(function (el) {
        el.textContent = total;
      });

      var seatEl = qs(overlay, '[data-summary-seat]');
      if (seatEl) seatEl.textContent = state.width + ' × ' + seatDepth + ' ' + unit;

      var composition = qs(overlay, '[data-composition-label]');
      if (composition) composition.textContent = compositionLabel();

      var priceEl = qs(overlay, '[data-live-price]');
      if (priceEl) priceEl.textContent = money(price());
    }

    qsa(root, '[data-atio-open]').forEach(function (button) {
      button.addEventListener('click', open);
    });

    qsa(overlay, '[data-atio-close]').forEach(function (button) {
      button.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !overlay.hidden) close();
    });

    qsa(overlay, '[data-range]').forEach(function (input) {
      input.addEventListener('input', function () {
        var type = input.dataset.range;
        state[type] = clamp(Number(input.value), Number(input.min), Number(input.max));
        state.dirtyDimensions = true;
        render();
      });
    });

    qsa(overlay, '[data-minus], [data-plus]').forEach(function (button) {
      button.addEventListener('click', function () {
        var type = button.dataset.minus || button.dataset.plus;
        var input = qs(overlay, '[data-range="' + type + '"]');
        var delta = button.dataset.minus ? -1 : 1;
        state[type] = clamp(state[type] + delta, Number(input.min), Number(input.max));
        state.dirtyDimensions = true;
        render();
      });
    });

    qsa(overlay, '[data-origin-marker]').forEach(function (button) {
      button.addEventListener('click', function () {
        var type = button.dataset.originMarker;
        state[type] = type === 'width' ? 140 : 107;
        state.dirtyDimensions = false;
        render();
      });
    });

    qs(overlay, '[data-toggle-measures]').addEventListener('click', function () {
      state.showDetails = !state.showDetails;
      this.setAttribute('aria-pressed', String(state.showDetails));
      render();
    });

    qs(overlay, '[data-order-toggle]').addEventListener('click', function () {
      var body = qs(overlay, '[data-order-body]');
      var open = body.hasAttribute('hidden');
      body.hidden = !open;
      this.setAttribute('aria-expanded', String(open));
    });

    /* 2D / 3D view switch — no-op if the toggle isn't rendered
       (gated behind the `show_3d_toggle` block setting). */
    if (viewToggleButtons.length) {
      viewToggleButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var nextView = btn.dataset.viewBtn === '3d' ? '3d' : '2d';
          if (nextView === state.view) return;
          state.view = nextView;
          syncViewVisibility();
          render();
        });
      });
    }

    /* ── Panel tabs (mobile) ──────────────────────────────────────────
    * Desktop: all four panels stay visible/stacked (see .atio-cfg__
    * tabpanel base rule in the CSS) — this logic only visibly changes
    * anything below the 980px breakpoint, where CSS shows just the
    * panel matching state.activeTab. Switching to the fabric tab
    * lazily triggers loadFabrics(), same lazy-load that used to fire
    * on first drawer open.
    * ─────────────────────────────────────────────────────────────── */
    var tabButtons = qsa(overlay, '[data-tab-btn]');
    var tabPanels = qsa(overlay, '[data-tab-panel]');

    function switchTab(tab) {
      if (!tab) return;
      state.activeTab = tab;

      tabButtons.forEach(function (btn) {
        var active = btn.dataset.tabBtn === tab;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', String(active));
      });

      tabPanels.forEach(function (panel) {
        panel.classList.toggle('is-active', panel.dataset.tabPanel === tab);
      });

      if (tab === 'fabric') loadFabrics(); /* no-op after first call */
    }

    tabButtons.forEach(function (btn) {
      btn.addEventListener('click', function () { switchTab(btn.dataset.tabBtn); });
    });

    /* ── Fabric drawer ─────────────────────────────────────────────
     * Fabrics are loaded lazily on first drawer open via the
     * /collections/{handle}/products.json endpoint (max 250/request).
     * This bypasses Liquid's 50-product collection limit entirely.
     *
     * Each fabric product can carry an `atio-material:{name}` tag
     * pointing at the real PBR material the 3D view should load for
     * that fabric (see parseMaterialTag() below). The collection
     * folder that material lives under is NOT part of this tag — it's
     * derived straight from the name by component-configurator-3d.js
     * (deriveMaterialCollection() there), so this file only ever needs
     * to carry the name around. Products without the tag still work
     * for the 2D swatch — state.materialName just stays unset, and
     * component-configurator-3d.js leaves the model on whatever
     * material it already has.
     *
     * Separately, each fabric product can carry an `atio-collection:
     * {name}` tag identifying which fabric collection it belongs to,
     * tracked on state.fabricCollection — falls back to the product's
     * product_type if that tag isn't present. NOTE: the current pricing
     * engine (see price() above) has no fabric term, so this is tracked
     * but not currently priced — flag if fabric grade should factor in.
     * ─────────────────────────────────────────────────────────────── */
    var fabricsLoaded = false;

    function parseMaterialTag(tags) {
      var prefix = 'atio-material:';
      var match = (tags || []).find(function (tag) { return tag.indexOf(prefix) === 0; });
      if (!match) return null;

      var material = match.slice(prefix.length).trim();
      return material || null;
    }

    /* Same idea as parseMaterialTag, but for the fabric's collection name
     * (see the note above — not currently consumed by pricing). Falls
     * back to the product's product_type if no atio-collection: tag is present. */
    function parseCollectionTag(tags, productType) {
      var prefix = 'atio-collection:';
      var match = (tags || []).find(function (tag) { return tag.indexOf(prefix) === 0; });
      if (match) {
        var collection = match.slice(prefix.length).trim();
        if (collection) return collection;
      }
      return productType || null;
    }

    function wireFabricButton(button, index) {
      if (index === 0 && !fabricsLoaded) {
        state.fabric      = button.dataset.title;
        state.fabricColor = button.dataset.color || '#d8d8d4';
        state.materialName = button.dataset.materialName || null;
        state.fabricCollection = button.dataset.collection || state.fabricCollection;
        button.classList.add('is-active');
      }

      button.addEventListener('click', function () {
        state.fabric       = button.dataset.title;
        state.fabricColor  = button.dataset.color || '#d8d8d4';
        state.materialName = button.dataset.materialName || null;
        state.fabricCollection = button.dataset.collection || state.fabricCollection;

        qsa(overlay, '[data-fabric-option]').forEach(function (item) {
          item.classList.toggle('is-active', item === button);
        });

        var fabricLabel   = qs(overlay, '[data-fabric-label]');
        var fabricDot     = qs(overlay, '[data-fabric-dot]');

        if (fabricLabel) fabricLabel.textContent = state.fabric;
        if (fabricDot) {
          var imgSrc = button.dataset.img;
          if (imgSrc) {
            fabricDot.style.background         = 'none';
            fabricDot.style.backgroundImage    = 'url(' + imgSrc + ')';
            fabricDot.style.backgroundSize     = 'cover';
            fabricDot.style.backgroundPosition = 'center';
          } else {
            fabricDot.style.background      = state.fabricColor;
            fabricDot.style.backgroundImage = '';
          }
        }

        render();
      });
    }

    function loadFabrics() {
      if (fabricsLoaded) return;

      var list    = qs(overlay, '[data-fabric-list]');
      var loading = qs(overlay, '[data-fabric-loading]');

      fetch('/collections/' + fabricCollectionHandle + '/products.json?view=fabrics-lite&limit=250')
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          fabricsLoaded = true;
          if (loading) loading.remove();

          var products = data.products || [];

          products.forEach(function (product, index) {
          var img    = product.images && product.images[0];
          var imgSrc = img ? img.src.replace(/(\.[^.?]+)(\?.*)?$/, '_240x240$1$2') : '';
          var color  = '#d8d8d4';
          var materialName = parseMaterialTag(product.tags);
          var collectionName = parseCollectionTag(product.tags, product.product_type);

          var button = document.createElement('button');
          button.type                 = 'button';
          button.className            = 'atio-cfg__fabric-option';
          button.dataset.fabricOption = '';
          button.dataset.title        = product.title;
          button.dataset.color        = color;
          if (imgSrc) button.dataset.img = imgSrc;
          if (product.title) button.dataset.materialName = product.title.split(" ")[0];
          if (collectionName) button.dataset.collection = collectionName;

          button.innerHTML =
            '<span class="atio-cfg__fabric-swatch">' +
              (imgSrc
                ? '<img src="' + imgSrc + '" alt="' + product.title.replace(/"/g, '&quot;') + '" loading="lazy" decoding="async">'
                : '<span class="atio-cfg__fabric-swatch-fallback" style="background:' + color + '"></span>'
              ) +
              '<span class="atio-cfg__fabric-check" aria-hidden="true">✓</span>' +
            '</span>' +
            '<span class="atio-cfg__fabric-name">' + product.title + '</span>';

          list.appendChild(button);
          wireFabricButton(button, index);
        });

          /* Wire up search now that the list is populated */
          var search = qs(overlay, '[data-fabric-search]');
          if (search) {
            search.addEventListener('input', function () {
              var term = search.value.toLowerCase();
              qsa(overlay, '[data-fabric-option]').forEach(function (b) {
                b.style.display = b.dataset.title.toLowerCase().indexOf(term) > -1 ? 'flex' : 'none';
              });
            });
          }

          // Push the resolved default (index-0) fabric/material into the
          // active view immediately, rather than waiting for the next
          // unrelated interaction to trigger a render().
          render();
        })
        .catch(function () {
          if (loading) loading.textContent = 'Errore nel caricamento. Riprova.';
        });
    }

    /*
    function openFabricDrawer() {
      qs(overlay, '[data-fabric-backdrop]').hidden = false;
      loadFabrics();
    }

    function closeFabricDrawer() {
      qs(overlay, '[data-fabric-backdrop]').hidden = true;
    }

    qs(overlay, '[data-open-fabrics]').addEventListener('click', openFabricDrawer);
    qs(overlay, '[data-close-fabrics]').addEventListener('click', closeFabricDrawer);

    qs(overlay, '[data-fabric-backdrop]').addEventListener('click', function (event) {
      if (event.target === this) closeFabricDrawer();
    });
    */

    qsa(overlay, '[data-open-inquiry]').forEach(function (button) {
      button.addEventListener('click', function () {
        state.requestType = button.dataset.requestType || 'info';

        var title = qs(overlay, '[data-inquiry-title]');
        var sub   = qs(overlay, '[data-inquiry-sub]');

        if (state.requestType === 'render') {
          if (title) title.textContent = 'Dove vuoi ricevere il render?';
          if (sub)   sub.textContent   = 'Ti invieremo una preview della configurazione scelta.';
        } else if (state.requestType === 'cart') {
          if (title) title.textContent = 'Dove vuoi ricevere la configurazione?';
          if (sub)   sub.textContent   = 'Ti invieremo il riepilogo della configurazione per completare l’ordine.';
        } else {
          if (title) title.textContent = 'Come preferisci essere contattato?';
          if (sub)   sub.textContent   = 'Invieremo la configurazione scelta insieme alla tua richiesta.';
        }

        switchTab('contact');

        // Scroll to contact section for desktop users
        var contactPanel = qs(overlay, '[data-tab-panel="contact"]');
        if (contactPanel) {
          contactPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    });

    qsa(overlay, '[data-contact-method]').forEach(function (button) {
      button.addEventListener('click', function () {
        state.contactMethod = button.dataset.contactMethod;

        qsa(overlay, '[data-contact-method]').forEach(function (item) {
          item.classList.toggle('is-active', item === button);
        });

        var wrap  = qs(overlay, '[data-contact-input-wrap]');
        var input = qs(overlay, '[data-contact-input]');

        wrap.hidden  = false;
        input.type   = state.contactMethod === 'email' ? 'email' : 'tel';
        input.placeholder = state.contactMethod === 'email' ? 'La tua email' : 'Il tuo numero WhatsApp';
        input.focus();
      });
    });

    qs(overlay, '[data-send-request]').addEventListener('click', function () {
      var input = qs(overlay, '[data-contact-input]');
      var value = input.value.trim();

      if (!value) {
        input.focus();
        return;
      }

      var body = [
        'Nuova richiesta configurazione',
        'Tipo richiesta: ' + (state.requestType === 'render' ? 'Richiedi render' : state.requestType === 'cart' ? 'Aggiungi al carrello' : 'Richiedi informazioni'),
        '',
        'Prodotto: ' + productTitle,
        'Composizione: ' + compositionLabel(),
        'Larghezza: ' + state.width + ' ' + unit,
        'Profondità totale: ' + state.depth + ' ' + unit,
        'Schienale: ' + state.width + ' × ' + backDepth + ' ' + unit,
        'Seduta: ' + state.width + ' × ' + (state.depth - backDepth) + ' ' + unit,
        'Rivestimento: ' + state.fabric,
        'Prezzo stimato: ' + money(price()),
        'Spedizione: ' + shippingDate,
        '',
        'Contatto preferito: ' + state.contactMethod,
        'Valore: ' + value,
        '',
        'Pagina: ' + window.location.href
      ].join('\n');

      if (!nativeForm) return;

      var nameField  = nativeForm.querySelector('[data-native-name]');
      var emailField = nativeForm.querySelector('[data-native-email]');
      var bodyField  = nativeForm.querySelector('[data-native-body]');

      if (nameField)  nameField.value  = state.contactMethod === 'whatsapp' ? 'WhatsApp: ' + value : value;
      if (emailField) emailField.value = state.contactMethod === 'email' ? value : 'quote@' + window.location.hostname;
      if (bodyField)  bodyField.value  = body;

      if (window.Shopify && window.Shopify.captcha && window.Shopify.captcha.protect) {
        window.Shopify.captcha.protect(nativeForm, function () {
          nativeForm.submit();
        });
      } else {
        nativeForm.submit();
      }
    });

    syncViewVisibility();
    render();

    ['width', 'depth'].forEach(function (type) {
      var valueEl = qs(overlay, '[data-' + type + '-value]');
      if (!valueEl) return;

      valueEl.addEventListener('change', function () {
        var input = qs(overlay, '[data-range="' + type + '"]');
        var clamped = clamp(Number(valueEl.value), Number(input.min), Number(input.max));
        state[type] = clamped;
        state.dirtyDimensions = true;
        render();
      });

      valueEl.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') valueEl.blur();
      });
    });
  }

  function boot() {
    document.querySelectorAll('[id^="AtioCfg-"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();